'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';

import { Rating, Badge, Switch } from '@mantine/core';

import { MediaImage } from '@/elements/media-image/media-image.component';

import { getEducationLecturersForDisplay } from '@/helpers/module-lecturers.helpers';
import { Plans } from '@/components/(education)/plans/plans.component';
import { Lecturers } from '@/components/(education)/lecturers/lecturers.component';
import { Modules } from '@/components/(education)/modules/modules.component';
import { TestimonialsWidget } from '@/components/(homepage)/testimonials-widget/testimonials-widget.component';
import { FAQ } from '@/components/(homepage)/faq/faq.component';

import { Icon } from '@/elements/icon/icon.component';
import { SocialShare } from '@/elements/social-share/social-share.component';
import { Button } from '@/elements/button/button.component';
import { RichText } from '@/elements/rich-text/rich-text.component';
import { VimeoPlayer as Vimeo } from '@/elements/vimeo-player/vimeo-player.component';
import { Message, Title } from '@/styles/global.elements';
import {
  Container,
  Heading,
  InfoBadges,
  VideoWrap,
  Content,
  Description,
  TablesWrap,
  InfoTable,
  InfoItem,
  SubtitleWrap,
  Subtitle,
  Section,
  PlansSection,
  SwitchLabel,
  Line,
  Grade,
  InfoContainer,
  SwitchContainer,
} from './education.elements';

import { useBanner } from '@/providers/banner.provider';
import { getRegistrationForEducation } from '@/actions/get-registration.action';
import { convertMinutes } from '@/utils/functions.utils';

import {
  IconPointFilled,
  IconSchool,
  IconScreenShare,
  IconVideo,
  IconClockHour3,
  IconCalendarPin,
  IconHexagons,
  IconRosetteDiscountCheck,
  IconAntennaBars1,
  IconAntennaBars2,
  IconAntennaBars3,
  IconAntennaBars4,
  IconAntennaBars5,
} from '@tabler/icons-react';

import { colors } from '@/styles/theme';

const videoQualityIndicators = (quality) => {
  const icons = {
    1: <IconAntennaBars1 size={28} color="pink" />,
    2: <IconAntennaBars2 size={28} color="red" />,
    3: <IconAntennaBars3 size={28} color="orange" />,
    4: <IconAntennaBars4 size={28} color="green" />,
    5: <IconAntennaBars5 size={28} color="teal" />,
  };

  return (
    <div style={{ position: 'relative', width: 28, height: 28 }}>
      <IconAntennaBars5
        size={28}
        style={{ position: 'absolute', zIndex: -1, top: 0, left: 0 }}
        color={colors.brown + 70}
      />
      {icons[quality]}
    </div>
  );
};

const Education = ({ education, testimonials, faq }) => {
  const [registration, setRegistration] = useState(null);
  const [_registrationLoading, setRegistrationLoading] = useState(true);

  const searchParams = useSearchParams();
  const actionType = searchParams.get('vrsta');

  const { setEducation } = useBanner();

  // Fetch registration client-side
  useEffect(() => {
    const fetchRegistration = async () => {
      try {
        const reg = await getRegistrationForEducation(education.slug);
        setRegistration(reg);
      } catch (error) {
        console.error('Failed to fetch registration:', error);
      } finally {
        setRegistrationLoading(false);
      }
    };

    fetchRegistration();
  }, [education.slug]);

  const registrationSubscriptionDynamic = registration?.subscriptions?.docs[0]?.dynamic;

  // Set education for discount banner
  useEffect(() => {
    setEducation(education);
    return () => setEducation(null);
  }, [education, setEducation]);

  const [dynamic, setDynamic] = useState(
    actionType === 'gift-education' || actionType === 'gift-card' ? 'total' : 'monthly'
  );

  // Update dynamic when registration loads
  useEffect(() => {
    if (
      registrationSubscriptionDynamic &&
      actionType !== 'gift-education' &&
      actionType !== 'gift-card'
    ) {
      setDynamic(registrationSubscriptionDynamic);
    }
  }, [registrationSubscriptionDynamic, actionType]);

  const {
    title,
    image,
    content,
    venues,
    modules: { modules, bonusModules, lecturerOrder },
    settings: { trailerId },
    learnings,
    plans: { educationPlans, subscriptionPlans },
    id,
    slug,
    type,
  } = education;

  const lecturers = getEducationLecturersForDisplay(modules, lecturerOrder);

  const nextLectureTime =
    modules
      .map((module) => new Date(module.lectureStartDateTime))
      .filter((date) => date > new Date())
      .sort((a, b) => a - b)[0] || null;

  const showToggleDynamicSubscription =
    subscriptionPlans.some(
      (plan) =>
        plan.plan.subscriptionDetails.priceAnnual !== plan.plan.subscriptionDetails.priceMonthly
    ) && registrationSubscriptionDynamic !== 'annual';

  const showToggleDynamicEducation = educationPlans.some((plan) => plan.installmentsAllowed);

  const badges = [];

  if (venues.includes('live')) {
    badges.push(
      <Badge
        size="lg"
        variant="transparent"
        color={colors.brown}
        leftSection={<IconSchool stroke={1} size={28} />}
      >
        uživo
      </Badge>
    );
  }

  if (venues.includes('online')) {
    badges.push(
      <Badge
        size="lg"
        variant="transparent"
        color={colors.brown}
        leftSection={<IconScreenShare stroke={1} size={28} />}
      >
        online
      </Badge>
    );
  }

  if (venues.includes('recording')) {
    badges.push(
      <Badge
        size="lg"
        variant="transparent"
        color={colors.brown}
        leftSection={<IconVideo stroke={1} size={28} />}
      >
        snimka
      </Badge>
    );
  }

  if (type === 'education') {
    badges.push(
      <Badge
        size="lg"
        variant="transparent"
        color={colors.brown}
        leftSection={<IconHexagons stroke={1} size={28} />}
      >
        Broj modula: {modules.length}
      </Badge>
    );
  }

  if (type === 'webinar' && modules[0].video.recordingId) {
    badges.push(
      <Badge
        size="lg"
        variant="transparent"
        color={colors.brown}
        leftSection={videoQualityIndicators(modules[0].video.videoQuality)}
      >
        Kvaliteta snimke
      </Badge>
    );
  }

  badges.push(
    <Badge
      size="lg"
      variant="transparent"
      color={colors.brown}
      leftSection={<IconClockHour3 stroke={1} size={28} />}
    >
      Trajanje: {convertMinutes(modules.reduce((sum, module) => sum + module.lectureDuration, 0))}
    </Badge>
  );

  badges.push(
    <Badge
      size="lg"
      variant="transparent"
      color={colors.brown}
      leftSection={<IconRosetteDiscountCheck stroke={1} size={28} />}
    >
      Certifikat i radni materijali
    </Badge>
  );

  if (nextLectureTime && (venues.includes('live') || venues.includes('online'))) {
    badges.push(
      <Badge
        size="lg"
        variant="transparent"
        color={colors.aqua}
        leftSection={<IconCalendarPin stroke={1} size={28} />}
      >
        Idući susret:{' '}
        {new Intl.DateTimeFormat('hr', {
          day: 'numeric',
          month: 'narrow',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(new Date(nextLectureTime))}
      </Badge>
    );
  }

  // Helper function to check if a plan offers features the user doesn't have
  const planHasUpgradeFeatures = (planFeatures, userRegistration) => {
    if (!userRegistration) return true;
    const features = ['hasCertificate', 'hasRecording', 'hasLive', 'hasOnline'];

    // Plan must have at least one feature the user doesn't have
    const hasNewFeature = features.some(
      (feature) => planFeatures[feature] && !userRegistration[feature]
    );

    // Plan must also have all features the user already has (so it's an upgrade, not downgrade)
    const hasAllExistingFeatures = features.every(
      (feature) => !userRegistration[feature] || planFeatures[feature]
    );

    return hasNewFeature && hasAllExistingFeatures;
  };

  // Filter subscription plans to only show upgrades
  const filteredSubscriptionPlans = registration
    ? subscriptionPlans.filter(({ plan }) => planHasUpgradeFeatures(plan.features, registration))
    : subscriptionPlans;

  // Filter education plans to only show upgrades (unless gifting)
  const filteredEducationPlans =
    registration && actionType !== 'gift-education'
      ? educationPlans.filter(({ plan }) => planHasUpgradeFeatures(plan.features, registration))
      : educationPlans;

  const showSubscriptionSection =
    actionType !== 'gift-education' &&
    actionType !== 'gift-card' &&
    filteredSubscriptionPlans.length > 0;

  const showEducationSection =
    actionType === 'gift-education' ? educationPlans.length > 0 : filteredEducationPlans.length > 0;

  return (
    <Container>
      <Heading>
        {trailerId ? (
          <VideoWrap>
            <Vimeo video={trailerId} responsive />
          </VideoWrap>
        ) : (
          <MediaImage
            variant="education"
            src={image?.sizes?.featuredImage?.url}
            width={1024}
            height={512}
            alt={image?.alt || 'Edukacija'}
            style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
          />
        )}
        <InfoContainer>
          <InfoBadges $numItems={badges.length}>
            {badges.map((badge, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                {badge}
              </div>
            ))}
          </InfoBadges>
          {(education.averageGrade < 5 && education.gradeCount >= 3) ||
          education.averageGrade > 2 ? (
            <Grade>
              {education.averageGrade.toFixed(2)}
              <Rating
                id={`education-detail-rating-${education.slug}`}
                size={'18px'}
                value={education.averageGrade}
                fractions={10}
                readOnly
              />
            </Grade>
          ) : null}
        </InfoContainer>
      </Heading>
      <Content>
        <Description>
          <RichText content={content} />
        </Description>
        <TablesWrap>
          {learnings.length > 0 ? (
            <InfoTable>
              <Title $medium>Kroz edukaciju ćeš naučiti:</Title>
              {learnings.map(({ id, item }) => (
                <InfoItem key={id}>
                  <Icon color={'blue'}>
                    <IconPointFilled />
                  </Icon>
                  {item}
                </InfoItem>
              ))}
            </InfoTable>
          ) : null}
        </TablesWrap>

        {registration && actionType !== 'gift-education' ? (
          <Section>
            <SubtitleWrap>
              <Subtitle $color="blue">Tvoja prijava</Subtitle>
              <Line $color="blue" />
            </SubtitleWrap>
            <Message>Ova edukacija je već dostupna unutar tvog profila.</Message>
            <Link href={`/moj-profil/${slug}`}>
              <Button filled width={'300px'} style={{ margin: '0 auto' }}>
                Pristupi edukaciji
              </Button>
            </Link>
          </Section>
        ) : null}

        <PlansSection>
          {showSubscriptionSection ? (
            <Section>
              <SubtitleWrap>
                <Subtitle $caps $color="simba">
                  {registration ? 'Unaprijedi pretplatu' : 'Pretplati se i uštedi'}
                </Subtitle>
                <Line $color="simba" />
              </SubtitleWrap>

              {showToggleDynamicSubscription ? (
                <SwitchContainer>
                  <SwitchLabel $active={dynamic === 'monthly'}>Mjesečno</SwitchLabel>
                  <Switch
                    size="lg"
                    color={colors.brown}
                    checked={dynamic === 'total' || dynamic === 'annual'}
                    onChange={(e) => setDynamic(e.target.checked ? 'annual' : 'monthly')}
                    withThumbIndicator={false}
                  />
                  <SwitchLabel $active={dynamic === 'annual'}>Godišnje</SwitchLabel>
                </SwitchContainer>
              ) : null}

              {registration ? (
                <Message>
                  Želiš unaprijediti svoju pretplatu s dodatnim značajkama? Odaberi napredni paket.
                  <br />
                  Iznos prve rate biti će umanjen za uplaćen a neiskorišten dio zadnje uplate.
                </Message>
              ) : null}

              <Plans
                subscriptionPlans={filteredSubscriptionPlans}
                education={{
                  id,
                  slug,
                  title,
                  type,
                  nextLectureTime: nextLectureTime ? nextLectureTime.getTime() : null,
                }}
                registration={registration}
                dynamic={dynamic}
              />
            </Section>
          ) : null}

          {showEducationSection ? (
            <Section>
              <SubtitleWrap>
                <Subtitle $caps $color="blue">
                  {actionType === 'gift-education'
                    ? 'Daruj edukaciju'
                    : registration
                      ? 'Unaprijedi pristup'
                      : subscriptionPlans.length > 0
                        ? `${actionType !== 'gift-card' ? 'Ili ' : ''}kupi edukaciju`
                        : 'Kupi edukaciju'}
                </Subtitle>
                <Line $color="blue" />
              </SubtitleWrap>

              {showToggleDynamicEducation &&
              filteredEducationPlans.length > 0 &&
              actionType !== 'gift-education' ? (
                <SwitchContainer>
                  <SwitchLabel $active={dynamic === 'monthly'}>Mjesečno</SwitchLabel>
                  <Switch
                    size="lg"
                    color={colors.brown}
                    checked={dynamic === 'total' || dynamic === 'annual'}
                    onChange={(e) => setDynamic(e.target.checked ? 'total' : 'monthly')}
                    withThumbIndicator={false}
                  />
                  <SwitchLabel $active={dynamic === 'total'}>Ukupno</SwitchLabel>
                </SwitchContainer>
              ) : null}

              {registration && actionType !== 'gift-education' ? (
                <Message>
                  Želiš unaprijediti svoju prijavu s dodatnim značajkama? Odaberi neki od
                  naprednijih paketa niže.
                </Message>
              ) : null}
              <Plans
                educationPlans={(actionType === 'gift-education'
                  ? educationPlans
                  : filteredEducationPlans
                ).map((plan) => ({
                  ...plan.plan,
                  educationDetails: { ...plan },
                }))}
                education={{
                  id,
                  slug,
                  title,
                  type,
                  nextLectureTime: nextLectureTime ? nextLectureTime.getTime() : null,
                  modules,
                }}
                registration={registration}
                dynamic={dynamic}
                horizontal
                actionType={actionType}
              />
            </Section>
          ) : null}
        </PlansSection>

        {modules.length > 1 ? (
          <Section>
            <SubtitleWrap>
              <Subtitle>Moduli</Subtitle>
              <Line />
            </SubtitleWrap>
            <Modules
              modules={modules}
              bonusModules={bonusModules}
              isCompact={actionType === 'gift-education'}
            />
          </Section>
        ) : null}

        <Section>
          <SubtitleWrap>
            <Subtitle>
              {lecturers.length === 1
                ? 'Predavač'
                : 'Predavači'}
            </Subtitle>
            <Line />
          </SubtitleWrap>
          <Lecturers
            lecturers={lecturers}
            type="bio"
            preserveOrder
          />
        </Section>

        {testimonials.length > 0 ? (
          <Section>
            <SubtitleWrap>
              <Subtitle>Što polaznici kažu o edukaciji</Subtitle>
              <Line />
            </SubtitleWrap>
            <TestimonialsWidget testimonials={testimonials} />
          </Section>
        ) : null}

        {faq.length > 0 ? (
          <Section>
            <FAQ faq={faq} />
          </Section>
        ) : null}

        <SocialShare title={title} />
      </Content>
    </Container>
  );
};

export { Education };
